import {Tr, TrSpace} from "@/i18n/i18n";
import SceneUtils from "@/utils/scene-utils";
import {lazyInject} from "@/container";
import {TYPES} from "@/types";
import {SceneService} from "@/services/scene-service";
import {Keys} from "@/constants/key";
import {Settings} from "@/constants/settings";
import {SceneName} from "@/model/scene-name";
import {ArenaLeague, BattleLog, FighterDto, LeaguesDto} from "@/store/arena/types";
import {SquareButton} from "@/scenes.ui/square-button";
import TextUtils from "@/utils/text-utils";
import {Listener} from "@/store/listener";
import ListenerManager from "@/managers/listener-manager";
import {MenuCreaturesNavPanel} from "@/scenes.ui/menu-creatures-nav-panel";
import OnceCaller from "@/utils/once-caller";
import {Creature} from "@/store/level/types";
import CreaturePanel from "@/scenes.ui/creature-panel";
import {GridUtils, PosType} from "@/utils/grid-utils";
import {Sizes} from "@/constants/size";
import {Lang, PayType, Player} from "@/store/player/types";
import TimeUtils from "@/utils/time-utils";
import {PaymentNotificationService} from "@/services/payment-notification-service";
import {MenuService} from "@/services/menu-service";
import {ShopService, TabName} from "@/services/shop-service";
import {OverchargePanel} from "@/scenes.ui/overcharge-panel";
import {Amount} from "@/model/amount";
import {HealthService} from "@/services/health-service";
import {ExperienceService} from "@/services/experience-service";
import {Fonts} from "@/constants/font";
import ResourceUtils from "@/utils/resource-utils";
import {ArenaHistoryPanel} from "@/scenes.ui/arena-history-panel";
import {ListView} from "@/scenes.ui/list-view";
import {ArenaHistoryView} from "@/scenes.ui/arena-history-view";
import PricePanel from "@/scenes.ui/price-panel";
import SoundUtils from "@/utils/sound-utils";
import ArenaStore from "@/store/arena";
import BattleStore from "@/store/battle";
import PlayerStore from "@/store/player";
import {ListService} from "@/services/list-service";
import {BottomPanel} from "@/scenes.ui/bottom-panel";
import {Link} from "@/scenes.ui/link";
import RewardedUtils from "@/utils/rewarded-utils";
import {RewardedAdvType} from "@/store/shop/types";
import {PaymentChangeService} from "@/services/payment-change-service";
import {IapService} from "@/services/iap-service";
import SpriteUtils from "@/utils/sprite-utils";
import CU from "@/utils/c-u";
import {ArenaHelpView} from "@/scenes.ui/arena-help-view";
import {ArenaLeaguePanel} from "@/scenes.ui/arena-league-panel";
import ArenaUtils from "@/utils/arena-utils";
import BBCodeText from "phaser3-rex-plugins/plugins/bbcodetext";
import {LeagueStatusPanel} from "@/scenes.ui/league-status-panel";
import {ArenaDailyRewardPanel} from "@/scenes.ui/arena-daily-reward-panel";
import {FeedbackPanel} from "@/scenes.ui/feedback-panel";
import {StringUtils} from "@/utils/string-utils";
import {ArenaSettings} from "@/scenes.ui/arena-settings";
import Scene = Phaser.Scene;
import Sprite = Phaser.GameObjects.Sprite;
import Text = Phaser.GameObjects.Text;
import Group = Phaser.GameObjects.Group;
import Container = Phaser.GameObjects.Container;
import {Reloadable} from "@/scenes/reloadable";

export default class ArenaScene extends Scene implements Listener, Reloadable {

    @lazyInject(TYPES.SceneService)
    private sceneService!: SceneService;

    @lazyInject(TYPES.ShopService)
    private shopService!: ShopService;

    @lazyInject(TYPES.ListService)
    private listService!: ListService;

    @lazyInject(TYPES.MenuService)
    private menuService!: MenuService;

    @lazyInject(TYPES.PaymentNotificationService)
    private paymentNotificationService!: PaymentNotificationService;

    @lazyInject(TYPES.HealthService)
    private healthService!: HealthService;

    @lazyInject(TYPES.ExperienceService)
    private experienceService!: ExperienceService;

    @lazyInject(TYPES.paymentChangeService)
    private paymentChangeService!: PaymentChangeService;

    @lazyInject(TYPES.IapService)
    private iapService!: IapService;

    private canvas!: HTMLCanvasElement;

    private background!: Sprite;

    private mainThemeSound!: Phaser.Sound.BaseSound | undefined;
    private selectSound!: Phaser.Sound.BaseSound | undefined;
    protected clickSound!: Phaser.Sound.BaseSound | undefined;
    protected completedSound!: Phaser.Sound.BaseSound | undefined;
    protected levelUpSound!: Phaser.Sound.BaseSound | undefined;
    protected awardSound!: Phaser.Sound.BaseSound | undefined;
    protected creatureClickSound!: Phaser.Sound.BaseSound | undefined;

    private changeButton!: SquareButton;
    private battleButton!: SquareButton;

    private myInfoBack: Sprite | undefined;
    private myName: Text | undefined;
    private myCupSign: Sprite | undefined;
    private myCups: Text | undefined;
    private myBattles: Text | undefined;
    private myLeague: Text | undefined;
    private myLeagueSign: Sprite | undefined;

    private grayedBackMain: Sprite | undefined;
    private grayedBackSecond: Sprite | undefined;

    private oppName: Text | undefined;
    private oppCupSign: Sprite | undefined;
    private oppCups: Text | undefined;
    private oppClan: Text | undefined;

    private victoryLabel: Text | undefined;
    private victoryRewards: Text[] | undefined;
    private victoryRewardIcons: Sprite[] | undefined;

    private defeatLabel: Text | undefined;
    private defeatCups: Text | undefined;
    private defeatCupsSign: Sprite | undefined;
    private defeatHealth: Text | undefined;
    private defeatHealthSign: Sprite | undefined;

    private bottomPanel!: BottomPanel;
    private menuNavPanel!: MenuCreaturesNavPanel;

    private enemyCreaturesGroup!: Group;
    private enemyCreaturePanels!: CreaturePanel[];

    private timeLabel!: Text;
    private timerId: number | undefined;

    private centralContainer: Container | undefined;

    constructor() {
        super({ key: 'ArenaScene' });
    }

    public preload(): void {
        SceneUtils.setCurrentScene(this);
        this.canvas = this.sys.game.canvas;

        this.load.image(Keys.IMG_ARENA_BACKGROUND, 'assets/bg/arena-back.jpg');
        this.load.image(Keys.ARENA_REFRESH, 'assets/arena/refresh.png');
        this.load.image(Keys.ARENA_TO_FIGHT, 'assets/arena/to_fight.png');
        this.load.image(Keys.ARENA_HISTORY, 'assets/arena/history.png');
        this.load.image(Keys.ARENA_BACK_MAIN, 'assets/arena/back_main.png');
        this.load.image(Keys.ARENA_BACK_SECOND, 'assets/arena/back_second.png');
        this.load.image(Keys.ARENA_HEART, 'assets/arena/heart.png');
        this.load.image(Keys.ARENA_QUESTION, 'assets/arena/question.png');
        this.load.image(Keys.DIVIDER_VERTICAL, 'assets/divider-vert.png');

        this.load.image(Keys.WHEEL_SPLASH, 'assets/wheel/splash.png');
    }

    public create(): void {
        let self = this;
        this.myInfoBack = undefined;
        this.myName = undefined;
        this.myCupSign = undefined;
        this.myCups = undefined;
        this.myBattles = undefined;
        this.myLeague = undefined;
        this.myLeagueSign = undefined;

        this.grayedBackMain = undefined;
        this.grayedBackSecond = undefined;

        this.oppName = undefined;
        this.oppCups = undefined;
        this.oppClan = undefined;
        this.oppCupSign = undefined;
        this.defeatLabel = undefined;
        this.victoryLabel = undefined;
        this.victoryRewards = undefined;
        this.victoryRewardIcons = undefined;
        this.defeatCups = undefined;
        this.defeatCupsSign = undefined;
        this.defeatHealth = undefined;
        this.defeatHealthSign = undefined;
        this.sceneService!.fadeBlackout(this, this.canvas!.width, this.canvas!.height);

        this.mainThemeSound = SoundUtils.addSound(this, Keys.MUSIC_SUN_SPARKLING_FOREST, {volume: Settings.MUSIC_VOLUME});
        this.selectSound = SoundUtils.addSound(this, Keys.SOUND_SELECT, {volume: Settings.MUSIC_VOLUME});
        this.selectSound = SoundUtils.addSound(this, Keys.SOUND_SELECT);
        this.clickSound = SoundUtils.addSound(this, Keys.SOUND_BUTTON_CLICK);
        this.completedSound = SoundUtils.addSound(this, Keys.SOUND_COMPLETED);
        this.levelUpSound = SoundUtils.addSound(this, Keys.SOUND_LEVEL_UP);
        this.awardSound = SoundUtils.addSound(this, Keys.SOUND_AWARD);
        this.creatureClickSound = SoundUtils.addSound(this, Keys.SOUND_CREATURE_CLICK, {volume: Settings.SHORT_TRACK_VOLUME});

        SoundUtils.play(this.mainThemeSound);

        this.background = SpriteUtils.addSprite(this, 0, -(860 - this.canvas.height / CU.S / 2)+57, Keys.IMG_ARENA_BACKGROUND)
            .setOrigin(0, 0);

        const xButton = SpriteUtils.addImage(this!, CU.ETALON_CANVAS_WIDTH * 0.95, CU.ETALON_CANVAS_WIDTH * 0.05, Keys.ATLAS_SHOP_UI, Keys.UI_SHOP_X).setOrigin(0.5, 0.5);

        xButton.setInteractive().on('pointerup', function() {
            SoundUtils.play(self.selectSound);
            self.switchScene(SceneName.ROAD_SCENE, {});
        });

        let player = PlayerStore.getPlayer()!;
        ArenaStore.ensureOpponent().then((fighter: FighterDto) => {
            if (!fighter || !fighter.creatures) {
                self.initializeLoading()
            } else if (!fighter.arenaOpensAt || TimeUtils.getServerTimeMs() > fighter.arenaOpensAt) {
                let yPos = self.initializeFighter(fighter);
                self.initializeButtons(yPos);
            } else {
                self.setupMyInfo(fighter, player);
                self.initializeTimer(player, fighter);
            }
            if(fighter && fighter.dailyReward) {
                let awards = new Map<string, number>([['mithril', fighter.dailyReward.gems], ['tickets', fighter.dailyReward.tickets]]);
                new ArenaDailyRewardPanel(self, self.canvas, fighter.dailyReward.leagueWas, fighter.dailyReward.placeWas, awards, () => {
                    ArenaStore.takeDailyReward().then(() =>{
                        new FeedbackPanel(self, Tr('nagrada.poluchena'), undefined, true).show();
                    });
                }).open();
            }
        });

        this.bottomPanel = new BottomPanel(this, 0, this.canvas.height, PlayerStore.getPlayer()!);
        this.add.existing(this.bottomPanel);
        ListenerManager.addPlayerListener(this);

        let myCreatures = PlayerStore.getCombativeCreatures();

        let calcX = this.canvas!.width - myCreatures.length * 30 * CU.S;
        calcX += myCreatures.length == MenuCreaturesNavPanel.MAX_CREATURES_NO_ARROWS ? 17 * CU.S : 0;

        this.menuNavPanel = new MenuCreaturesNavPanel(this, this.canvas!, calcX, this.canvas!.height - 80 * CU.S,
            myCreatures, (sceneName: SceneName, { creatureIndex: creatureIndex} ) => {
                OnceCaller.doOnce("toCreatureScene", () => {
                    self.switchScene!(SceneName.CREATURE_SCENE, { creatureIndex: creatureIndex});
                });
            }, MenuCreaturesNavPanel.MAX_CREATURES_NO_ARROWS);

        this.add.existing(this.menuNavPanel);

        this.experienceService.setSceneAndCanvas(this, this.canvas, this.awardSound, this.creatureClickSound, (enabled: boolean) => {});
        this.healthService.initHealthTimer(this, this.canvas, () => this.openShop(),
            this.clickSound, this.levelUpSound, this.awardSound, (y: number) => this.experienceService.changeChildrenPosition(y));

        let historyButton = SpriteUtils.addImage(this, 40, 40, Keys.ARENA_HISTORY);
        let questionButton = SpriteUtils.addImage(this, 40, 40 + 70, Keys.ARENA_QUESTION);

        historyButton.setInteractive().on('pointerup', () => {

            SoundUtils.play(this.clickSound);

            this.centralContainer = new Container(this);
            this.centralContainer.setDepth(3000);
            this.add.existing(this.centralContainer);

            ArenaStore.getBattleLog().then((logs: BattleLog[]) => {

                let historyPanelContainers: Container[] = [];

                let historyView = new ArenaHistoryView(this, this.canvas, () => {
                    this.centralContainer!.destroy();
                });

                historyView.open();


                logs.forEach(l => {
                    let panel: ArenaHistoryPanel = new ArenaHistoryPanel(this, this.canvas, l, true);
                    historyPanelContainers.push(panel);
                })

                let listView: ListView = new ListView(this, this.canvas!, historyPanelContainers, 30 * CU.S, this.canvas.height / 2   - 275 * CU.S, 535 * CU.S, 570 * CU.S);

                self.centralContainer!.add([historyView, listView]);

            });
        })

        questionButton.setInteractive().on('pointerup', () => {

            SoundUtils.play(this.clickSound);

            this.centralContainer = new Container(this);
            this.centralContainer.setDepth(3000);
            this.add.existing(this.centralContainer);

            ArenaStore.getLeaguesDto().then((leaguesDto: LeaguesDto) => {
                initializeLeaguePanel(leaguesDto)
            });

            function initializeLeaguePanel(leaguesDto: LeaguesDto) {
                let helpPanelContainer: Container[] = [];

                let helpView = new ArenaHelpView(self, self.canvas, () => {
                    self.centralContainer!.destroy();
                });

                helpView.open();

                let mskRewardHours = leaguesDto.rewardHourMsk;
                let mskUTCOffset = -3;
                let rewardTime = new Date();
                rewardTime.setUTCHours(mskRewardHours + mskUTCOffset);
                let rewardHour = rewardTime.getHours();
                let rewardTimeInfo = StringUtils.formatString(Tr('arena.info.3'),rewardHour +':00');
                let remainSeconds = leaguesDto.remainMillisToReward/1000;
                if(remainSeconds <0) remainSeconds = 0;

                let arenaInfo = new BBCodeText(self, 0, 15,
                    '[color=#ffd460]' + '[size=30]•[/size]  ' + Tr('arena.info.1') + '[/color]\n\n' +
                    '[color=#ff8932]' + '[size=30]•[/size]  ' + Tr('arena.info.2') + '[/color]\n\n' +
                    '[color=#ffd460]' + '[size=30]•[/size]  ' + Tr('arena.info.4') + '[/color]\n\n' +
                    '[color=#ff8932]' + '[size=30]•[/size]  ' + Tr('arena.info.5') + '[/color]\n\n' +
                    '[color=#ffd460]' + '[size=30]•[/size]  ' + rewardTimeInfo  +
                    TimeUtils.getDurationTextSimple(remainSeconds).toLowerCase() + '[/color]', {
                        fontFamily: Fonts.MYRIADPRO_REGULAR,
                        fontSize: '15pt',
                        align: 'left',
                        wrap: {
                            mode: 1,
                            width: 500
                        }
                    }).setScale(CU.S, CU.S).setOrigin(0.5, 0);

                let arenaInfo2 = new BBCodeText(self, arenaInfo.getBottomLeft().x, arenaInfo.getBottomLeft().y  + 2 * CU.S,
                    '\[[u]' + Tr('change') + '[/u]\]', {
                        fontFamily: Fonts.MYRIADPRO_REGULAR,
                        fontSize: '15pt',
                        align: 'left',
                        color: '#ffd460',
                        underline: {
                            color: '#ffd460',
                            thickness: 2,
                            offset: 5,
                        },
                        wrap: {
                            mode: 1,
                            width: 500
                        }
                    }).setScale(CU.S, CU.S).setOrigin(0, 0);
                let cont = new Container(self);

                arenaInfo2.setInteractive().on('pointerup', function(pointer: any) {
                    new ArenaSettings(self, self.canvas, rewardHour, (newRewardHour) => {
                        ArenaStore.setRewardTimeOffset(newRewardHour - rewardHour + mskRewardHours).then((remainMillis) => {
                            new FeedbackPanel(self, Tr('izmeneno'), undefined, true).show();

                            ArenaStore.getLeaguesDto().then((leaguesDto: LeaguesDto) => {
                                initializeLeaguePanel(leaguesDto)
                            });
                        });

                    }).open();
                    helpView.close();
                    leagueStatusPanel.destroy();
                    listView.destroy();
                });

                cont.height = arenaInfo.height + 65;
                cont.add(arenaInfo)
                cont.add(arenaInfo2)
                helpPanelContainer.push(cont);

                leaguesDto.leagues.forEach(l => {
                    let panel: ArenaLeaguePanel = new ArenaLeaguePanel(self, self.canvas, l, true);
                    helpPanelContainer.push(panel);
                })

                let listView: ListView = new ListView(self, self.canvas!, helpPanelContainer, 30 * CU.S, self.canvas.height / 2 - 180 * CU.S, 535 * CU.S, 475 * CU.S);
                let leagueStatusPanel = new LeagueStatusPanel(self, self.canvas, leaguesDto.myLeague, leaguesDto.myPosition,
                    leaguesDto.myBestLeague, leaguesDto.myBestPosition)
                self.centralContainer!.add([helpView, listView, leagueStatusPanel]);
            }
        })

    }

    public update(): void {
        ListenerManager.callUpdateListeners();
    }

    onChange(): void {
        if (this.scene && this.scene.isActive()) {
            this.bottomPanel.setBalance(PlayerStore.getPlayer()!);
        }
    }

    private initializeButtons(startY: number) {
        let self = this;
        let yPos = startY + 70;
        this.changeButton = new SquareButton(this, 200, yPos, -10, Keys.UI_ART_BROWN_BUTTON,
            undefined, Keys.ARENA_REFRESH, Tr('smenit'), false, 68 * CU.S, 68 * CU.S);
        let changePrice = new Amount();
        changePrice.gold  = ArenaStore.getOpponent().changeOpponentPrice;
        this.changeButton.setCallback(() => {
            let fighter = ArenaStore.getOpponent();
            if (fighter.changeOverCharge > 0) {
                SoundUtils.play(this.selectSound);
                let aop: OverchargePanel = new OverchargePanel(self, self.canvas!,
                    300 * CU.S, this.canvas.height / 2 - 30, changePrice, fighter.changeOverCharge, () => {
                        if (fighter.changeOverCharge <= PlayerStore.getPlayer()!.mithril) {
                            aop.close();
                            SoundUtils.play(self.clickSound);
                            self.doChangeOpponent();
                        } else {
                            aop.close();
                            SoundUtils.play(this.levelUpSound);
                            self.openShop();
                        }
                    });
                aop.open();
            } else {
                SoundUtils.play(self.selectSound);
                self.doChangeOpponent();
            }
        });
        this.battleButton = new SquareButton(this, 400, yPos, 0, Keys.UI_ART_GREEN_BUTTON,
            undefined, Keys.ARENA_TO_FIGHT, Tr('v.boy'), false, 110 * CU.S, 110 * CU.S);
        this.battleButton.setCallback(() => {
            OnceCaller.doOnce('attackArena', () => {
                SoundUtils.play(self.creatureClickSound);
                BattleStore.attackArena().then(() => {
                    self.switchScene(SceneName.BATTLE_SCENE, {});
                });
            });
        });
        this.add.existing(this.changeButton);
        this.add.existing(this.battleButton);

        const pricePanel: PricePanel = new PricePanel(this, this.changeButton.x + 48 * CU.S, this.changeButton.y + 54 * CU.S, changePrice);
        pricePanel.setOrigin(0.5, 0.5);
        this.add.existing(pricePanel);
    }

    private doChangeOpponent() {
        let self = this;
        ArenaStore.changeOpponent().then((fighter: FighterDto) => {
            self.initializeFighter(fighter);
        });
    }

    private initializeFighter(fighter: FighterDto): number {



        let self = this;

        if (this.enemyCreaturePanels) {
            this.enemyCreaturePanels.forEach(p => p.destroy());
        }

        if (this.enemyCreaturesGroup) {
            this.enemyCreaturesGroup.destroy();
        }

        this.enemyCreaturePanels = [];
        this.enemyCreaturesGroup = this.add.group();

        fighter.creatures.forEach((creature: Creature) => {
            let panel = new CreaturePanel(this, 0, 0, creature, () => {}, false, true);
            panel.setHealthVisible();
            panel.setCardShadeVisible();
            self.add.existing(panel);
            self.enemyCreaturesGroup.add(panel);
            self.enemyCreaturePanels.push(panel);
        });

        let rockCenterY = this.getRockCenter();
        GridUtils.gridAlign(this.enemyCreaturesGroup, rockCenterY * CU.S - Sizes.CELL_HEIGHT / 2 * CU.S, this, false, PosType.LINE);

        if (!this.grayedBackMain) {
            this.grayedBackMain = SpriteUtils.addSprite(this, 300, rockCenterY + 20, Keys.ARENA_BACK_MAIN, undefined, 1, 1.8).setOrigin(0.5, 0);
        }

        this.oppName = this.setText(this.oppName, Tr('protivnik').toLowerCase() + ': ' + fighter.opponentName + Tr('level.braket') + fighter.opponentLevel + ')', (scene: Scene, content: string) => {
            return TextUtils.addTextAdvanced(scene, content, 300, this.grayedBackMain!.y / CU.S + 50,
                '#B9FFAD', 18, 'bold', 500, 'center', 2, Fonts.MYRIADPRO_REGULAR);
        });

        this.oppCups = this.setText(this.oppCups, `${fighter.opponentPosition}${Tr('th.place')}`, (scene: Scene, content: string) => {
            return TextUtils.addTextAdvanced(scene, content, 312, this.oppName!.y / CU.S + 35,
                '#ffffff', 18, 'normal', 280, 'center', 2, Fonts.MYRIADPRO_REGULAR);
        });

        let cll = fighter.opponentClan ? (fighter.opponentClan + (fighter.isMyClan ? (' (' + Tr('sm.cln') + ')') : '')) : '-';
        this.oppClan = this.setText(this.oppClan, Tr('kln') + cll, (scene: Scene, content: string) => {
            return TextUtils.addTextAdvanced(scene, content, 300, this.oppCups!.y / CU.S + 34,
                '#a9dc9d', 14, 'italic', 280, 'center', 2, Fonts.MYRIADPRO_REGULAR);
        });

        if (this.oppCupSign) {
            this.oppCupSign.destroy();
        }
        this.oppCupSign = SpriteUtils.addSprite(this, this.oppCups.getLeftCenter().x! / CU.S - 18, this.oppCups.y / CU.S - 2, Keys.ATLAS_LEAGUE_SMALL, ArenaUtils.getLeagueImgSmall(fighter.opponentLeague)).setOrigin(0.5, 0.5).setScale(1);

        this.myBattles = this.setText(this.myBattles, `${Tr('fights')}: ${fighter.arenaCurrBattles}/${fighter.arenaMaxBattles}`, (scene: Scene, content: string) => {
            return TextUtils.addTextAdvanced(scene, content, 300, this.grayedBackMain!.y / CU.S + 20,
                '#e5e3e3', 16, 'normal', 280, 'center', 2, Fonts.MYRIADPRO_REGULAR);
        });

        let secBackY = this.grayedBackMain.getBottomCenter().y! / CU.S;
        if (!this.grayedBackSecond) {
            this.grayedBackSecond = SpriteUtils.addSprite(this, 300, secBackY!, Keys.ARENA_BACK_SECOND, undefined, 1, 0.5).setOrigin(0.5, 0);
        }

        let awards: [string, any][] = Object.entries(fighter.reward);
        let awardsSize = 0;
        let rewardType: string, rewardValue: number;
        for ([rewardType, rewardValue] of awards) {
            if (rewardValue > 0) {
                ++awardsSize;
            }
        }
        let resLabelOffsetY = awardsSize >= 5 ? 15 : 20;
        let awardOffsetY = awardsSize >= 5 ? 29 : 35;

        let victoryX = 170;
        this.victoryLabel = this.setText(this.victoryLabel, `${Tr('pobeda')}:`, (scene: Scene, content: string) => {
            return TextUtils.addTextAdvanced(scene, content, victoryX, this.grayedBackSecond!.y / CU.S + resLabelOffsetY + 17,
                '#DFFFE3', 17, 'normal', 280, 'left', 2, Fonts.MYRIADPRO_REGULAR);
        });
        let offsetY = (awardsSize >= 5 ? 45 : 50) - 29;
        if (this.victoryRewardIcons) {
            this.victoryRewardIcons.forEach(r => r.destroy());
        }
        if (this.victoryRewards) {
            this.victoryRewards.forEach(r => r.destroy());
        }
        this.victoryRewards = [];
        this.victoryRewardIcons = [];
        let defeatX = 350;

        for ([rewardType, rewardValue] of awards) {
            if(rewardType != 'elixir' && rewardType != 'crystal' && rewardType != 'mithril' && rewardType != 'tickets'){
                continue;
            }

            if (rewardValue > 0 ) {
                let atlas = ResourceUtils.findRewardTypeAtlas(rewardType);
                let rewardTypeIcon;
                if (atlas) {
                    rewardTypeIcon = SpriteUtils.addSprite(this, defeatX + 12, secBackY! + offsetY, atlas, ResourceUtils.findRewardTypeIcon(rewardType));
                    SpriteUtils.setDispSize(rewardTypeIcon, 38 * CU.S, 38 * CU.S);
                } else {
                    rewardTypeIcon = SpriteUtils.addSprite(this, defeatX + 12, secBackY! + offsetY, ResourceUtils.findRewardTypeIcon(rewardType));
                    SpriteUtils.setDispSize(rewardTypeIcon, 38 * CU.S, 38 * CU.S);
                }

                let rewardValueText = TextUtils.addTextAdvanced(this, '+' + TextUtils.formatMoney(TextUtils.trimDigitsAfter(rewardValue, 3)), rewardTypeIcon.x / CU.S + 20, rewardTypeIcon!.y / CU.S,
                    '#dfffe3', 17, 'bold', 280, 'left', 2, Fonts.MYRIADPRO_REGULAR);

                this.victoryRewardIcons!.push(rewardTypeIcon);
                this.victoryRewards!.push(rewardValueText);

                offsetY += awardOffsetY;
            }
        }



        let player = PlayerStore.getPlayer()!;
        this.setupMyInfo(fighter, player);

        let p = fighter.penalty;

        return this.grayedBackSecond.getBottomCenter().y! / CU.S;
    }

    private initializeTimer(player: Player, fighter: FighterDto) {
        let self = this;
        if (this.timerId) {
            clearInterval(this.timerId);
            this.timerId = undefined;
        }

        let rockCenterY = this.getRockCenter();
        if (!this.grayedBackMain) {
            this.grayedBackMain = SpriteUtils.addSprite(this, 300, rockCenterY + 20, Keys.ARENA_BACK_MAIN, undefined, 1, 1.4).setOrigin(0.5, 0);
        }

        TextUtils.addTextAdvanced(this, Tr('esche') + String(player.arenaMaxBattles) + ' ' + Tr('fights.in') + ':', 300, this.grayedBackMain.y / CU.S + 30,
            '#ffffff', 17, 'normal', 500, 'center', 2, Fonts.MYRIADPRO_REGULAR);

        let remainSec = ArenaScene.remainSec(fighter);
        let durText = TimeUtils.getDurationText(remainSec);

        this.timeLabel = TextUtils.addTextAdvanced(this, durText, 300, this.grayedBackMain.y / CU.S + 70,
            '#ffde09', 24, 'normal', 500, 'center', 2, Fonts.MYRIADPRO_REGULAR);

        this.timerId = setInterval(() => {
            if (this.timeLabel && this.timeLabel.active && this.scene!.isActive()) {
                let remainSec = ArenaScene.remainSec(fighter);
                let durText = TimeUtils.getDurationText(remainSec);
                this.timeLabel.setText(durText);
                if (remainSec <= 0) {
                    clearInterval(self.timerId);
                    this.switchScene(SceneName.ARENA_SCENE, {});
                }
            } else if (this.timerId) {
                clearInterval(self.timerId);
                self.timerId = undefined;
            }
        }, 1000);

        let speedupButton = new SquareButton(this, 200, this.grayedBackMain.y / CU.S + 200, 0, Keys.UI_ART_GREEN_BUTTON,
            undefined, Keys.IC_100_MITHRIL, Tr('uskorit'), false);

        speedupButton.setCallback(() => {
            if (fighter.speedupPrice <= PlayerStore.getPlayer()!.mithril) {
                SoundUtils.play(self.clickSound);
                ArenaStore.speedupArena(PayType.GEMS).then(() => {
                    self.switchScene(SceneName.ARENA_SCENE, {});
                });
            } else {
                SoundUtils.play(this.levelUpSound);
                self.openShop();
            }
        });
        this.add.existing(speedupButton);

        if (fighter.rewardedEnabled && fighter.rewardedWatchesToday < fighter.rewardedWatchesMax) {

            let label1 = TextUtils.addTextAdvanced(this, Tr('or.get'), 300, this.grayedBackMain.y / CU.S + 366-45,
                '#D0D0D0', 12, 'normal', 280, 'center', 2, Fonts.MYRIADPRO_REGULAR);
            this.add.existing(label1);

            let label2 = TextUtils.addTextAdvanced(this, TrSpace('pl.btl'), Settings.LANG_TYPE === Lang.EN ? 255 : 215, this.grayedBackMain.y / CU.S + 390-45,
                '#efefef', 12, 'normal', 280, 'center', 2, Fonts.MYRIADPRO_REGULAR);
            this.add.existing(label2);

            let label3 = TextUtils.addTextAdvanced(this, TrSpace('do') + String(fighter.rewardedWatchesMax)
                + TrSpace('t.p.d') + ' (' + String(fighter.rewardedWatchesToday) + ' / ' + String(fighter.rewardedWatchesMax) + ')',
                300, this.grayedBackMain.y / CU.S + 418-45,
                '#D0D0D0', 12, 'normal', 280, 'center', 2, Fonts.MYRIADPRO_REGULAR);
            this.add.existing(label1);

            let link: Link = new Link(this, label2.getRightCenter().x! + 5 * CU.S, this.grayedBackMain.y + (380-45) * CU.S, Tr('w.adv'), () => {
                SoundUtils.play(self.clickSound);
                RewardedUtils.watchRewarded(self, RewardedAdvType.SPEEDUP_ARENA, () => {
                    ArenaStore.speedupArena(PayType.ADV).then(() => {
                        self.switchScene(SceneName.ARENA_SCENE, {});
                    });
                });
            }, '#efefef');

            this.add.existing(label1);
            this.add.existing(label2);
            this.add.existing(label3);
            this.add.existing(link);
        }

        let speedupPriceText = TextUtils.addText(this,
            String(fighter.speedupPrice), 200, this.grayedBackMain.y / CU.S + 234, '#ffffff', 25, 'bold', 200);
        this.add.existing(speedupPriceText);

        let roadButton = new SquareButton(this, 400, this.grayedBackMain.y / CU.S + 200, 0, Keys.UI_ART_BROWN_BUTTON,
            Keys.ATLAS_PROFILE, Keys.UI_GOLDEN_CHECK, Tr('k.doroge'), false);
        roadButton.setCallback(() => {
            SoundUtils.play(self.selectSound);
            self.switchScene(SceneName.ROAD_SCENE, {});
        })
        this.add.existing(roadButton);
    }

    private initializeLoading() {
        TextUtils.addText(this, Tr('arena.esche.ne.otkrilas.podozhdite.nemnogo'), 300, 350, '#ffffff', 24, 'normal', 400);

        let speedupButton = new SquareButton(this, 300, 600, 0, Keys.UI_ART_GREEN_BUTTON,
            Keys.ATLAS_PROFILE, Keys.UI_GOLDEN_CHECK, Tr('obnovit'), false);

        let self = this;
        speedupButton.setCallback(() => {
            SoundUtils.play(self.clickSound);
            self.switchScene(SceneName.ARENA_SCENE, {});
        });
        this.add.existing(speedupButton);
    }

    private setupMyInfo(fighter: FighterDto, player: Player) {
        if (!this.myInfoBack) {
            this.myInfoBack = SpriteUtils.addSprite(this, 300, -5, Keys.ARENA_HEAD, undefined, 1.1, 1.30).setOrigin(0.5, 0);
        }

        this.myName = this.setText(this.myName, player.name, (scene: Scene, content: string) => {
            return TextUtils.addTextAdvanced(scene, content, 300, 14,
                '#ffd754', 16, 'normal', 280, 'center', 2, Fonts.MYRIADPRO_REGULAR);
        });

        this.myCups = this.setText(this.myCups, `${fighter.myPosition}${Tr('th.place')}`, (scene: Scene, content: string) => {
            return TextUtils.addTextAdvanced(scene, content, 300, this.myName!.y / CU.S + 22,
                '#ffffff', 14, 'normal', 280, 'center', 2, Fonts.ARIAL_MT);
        });

        if (!this.myCupSign) {
            let longName = this.myName!.text.length > 12;
            this.myLeagueSign = SpriteUtils.addSprite(this, 210, this.myCups.y / CU.S + 4 + (longName? 8 : 0),
                Keys.ATLAS_LEAGUE_SMALL, Keys.LEAGUE_SHADOW).setOrigin(0.5, 0.5).setScale(longName? 1 : 1.2).setAlpha(0.7);
            this.myCupSign = SpriteUtils.addSprite(this, 210, this.myCups.y / CU.S - 2+(longName? 10 : 0),
                Keys.ATLAS_LEAGUE_SMALL, ArenaUtils.getLeagueImgSmall(fighter.myLeague), longName? 1 : 1.2).setOrigin(0.5, 0.5);
        }

        this.myLeague = this.setText(this.myLeague, ArenaUtils.getLeagueName(fighter.myLeague), (scene: Scene, content: string) => {
            return TextUtils.addTextAdvanced(scene, content, 300, this.myCups!.y / CU.S + 22,
                ArenaUtils.getLeagueColor(fighter.myLeague), 14, 'normal', 280, 'center', 2, Fonts.ARIAL_MT);
        });
    }

    private switchScene(sceneName: SceneName, params: any) {
        ListenerManager.removePlayerListener(this);
        this.experienceService.stopListenPlayer();
        SoundUtils.stop(this.mainThemeSound);
        this.sceneService.startSceneWithParams(this, params, sceneName, this.canvas!.width, this.canvas!.height);
    }

    private static remainSec(fighter: FighterDto): number {
        return Math.floor((fighter.arenaOpensAt - TimeUtils.getServerTimeMs()) / 1000);
    }

    private openShop() {
        let self = this;
        this.shopService.openShop(this, this.canvas, true, () => {
            self.menuService.destroyMenu();
        }, self.paymentNotificationService!, (creatureId: string) => {
            self.listService.showManual(this, this.canvas, creatureId, () => {});
         }, TabName.BANK, this.listService, this.paymentChangeService, this.iapService);
        this.bottomPanel.setDepth(4000);
        this.menuNavPanel.setDepth(4000);
        this.menuService.setShowNavPanel(true);
        this.menuService.onChange();
    }

    private setText(textComponent: Text | undefined, content: string, createText: (scene: Scene, content: string) => Text) {
        if (!textComponent) {
            textComponent = createText(this, content);
            this.add.existing(textComponent);
        } else {
            textComponent.setText(content);
        }
        return textComponent;
    }

    private getRockCenter() {
        return this.canvas.height / CU.S / 2 - 160 + 57;
    }

    public reload(): void {
        this.sceneService.startSceneInstant(this, SceneName.ARENA_SCENE);
    }
}
